import { useAuth } from '@hooks/useAuth';
import { useOneSignalPush } from '@hooks/useOneSignalPush';
import { useWebPushPermissionPrompt } from '@hooks/useWebPushPermissionPrompt';
import { usePushPromptChoreography } from '@hooks/usePushPromptChoreography';
import { usePushMigrationStatus } from '@hooks/usePushMigrationStatus';
import { useLegacyWebPushTeardown } from '@hooks/useLegacyWebPushTeardown';
import { useOneSignalNotificationEvents } from '@hooks/useOneSignalNotificationEvents';
import { useSwNotificationNavigate } from '@hooks/useSwNotificationNavigate';
import { useSwChatMessageRefresh } from '@hooks/useSwChatMessageRefresh';
import { WebPushPermissionDrawer } from './WebPushPermissionDrawer';

/**
 * Push initializer for the OneSignal-owns-push path (VITE_ONESIGNAL_PUSH_OWNER).
 * Mounted by WebPushInitializer's env-static chooser instead of the legacy body.
 *
 * Reuses the exact permission-prompt drawer + coordination choreography as the
 * legacy path (the shared `usePushPromptChoreography`), but drives it through
 * the OneSignal opt-in adapter (`useOneSignalPush`). Two deliberate
 * differences from the legacy initializer:
 *
 *   - NO auto-subscribe effect. OneSignal's `autoResubscribe: true` (set in
 *     initOneSignal) already re-opts-in returning users who granted permission,
 *     so a manual "permission granted but not subscribed → subscribe" effect
 *     would be redundant (and would fight the SDK).
 *   - NO VAPID rotation hook. Rotation is a legacy-VAPID concern; OneSignal
 *     manages its own subscription lifecycle.
 *
 * It additionally runs `useLegacyWebPushTeardown` (hands this device off the
 * legacy pipeline once OneSignal's subscription is confirmed AND the backend
 * reports OneSignal delivery live — see the gate below) and keeps
 * `useSwNotificationNavigate` mounted — legacy VAPID pushes can still arrive
 * before a device's legacy subscription is torn down, and that relay is
 * transport-agnostic. It also mounts `useOneSignalNotificationEvents`, which
 * re-provides page-side the two behaviours the root SW gave legacy pushes but
 * OneSignal's own worker now owns for migrated browsers: chat-focus suppression
 * and safe click navigation. Finally it mounts `useSwChatMessageRefresh` so that,
 * during the dual-send overlap, a legacy chat push the root SW suppresses still
 * refreshes chat caches inline on the owner path (the legacy listener lives in
 * `useWebPushNotifications`, which is not mounted here).
 */
export function OneSignalPushInitializer() {
  const { isAuthenticated } = useAuth();
  const oneSignalPush = useOneSignalPush();
  const prompt = useWebPushPermissionPrompt(oneSignalPush);
  useSwNotificationNavigate();

  // Re-provide the legacy SW's chat-focus suppression + safe click navigation for
  // notifications OneSignal's scoped worker (not the root SW) now displays.
  useOneSignalNotificationEvents();

  // During the dual-send overlap a device can still be legacy-VAPID-subscribed, so
  // the root SW keeps relaying CHAT_MESSAGE_RECEIVED for suppressed legacy chat
  // pushes. The legacy listener lives in useWebPushNotifications (not mounted here),
  // so mount this owner-path relay to refresh chat caches inline for that window.
  useSwChatMessageRefresh();

  // Hand this browser off the legacy VAPID pipeline once OneSignal owns push,
  // so the device isn't reachable by both paths during the dual-send overlap.
  // Three-part gate — teardown deletes this device's only working delivery
  // path, so every leg must be TRUE before it runs:
  //   - isSubscribed: OneSignal reports this browser opted in (client-local).
  //   - isAuthenticated: the teardown's backend DELETE (unsubscribeFromPush)
  //     is authenticated, but OneSignal's autoResubscribe opts devices in
  //     regardless of login — an unauthed returning visitor would otherwise
  //     fire failing 401 DELETEs plus teardown-failed telemetry noise.
  //   - backend delivery-readiness: `optedIn` alone doesn't mean anything is
  //     SENDING via OneSignal yet. In the owner-flag-on / backend-flag-off
  //     rollout window, tearing down on opt-in alone would leave this device
  //     reachable by NEITHER path (legacy row deleted, OneSignal dormant) —
  //     zero delivery until the backend flag flips. So wait for the backend
  //     to report ONESIGNAL_PUSH_ENABLED; until then the device stays on the
  //     legacy pipeline (worst case: duplicates during the overlap, never
  //     silence). Loading/error/absent-endpoint all read as false (fail-safe).
  // Gated devices retry teardown on a later visit once every leg holds.
  const { data: migrationStatus } = usePushMigrationStatus({
    enabled: oneSignalPush.isSubscribed && isAuthenticated,
  });
  useLegacyWebPushTeardown(
    oneSignalPush.isSubscribed &&
      isAuthenticated &&
      migrationStatus?.onesignal_send_enabled === true,
  );

  // Shared opt-in choreography (10s delay + cross-prompt coordination),
  // identical to the legacy initializer by construction.
  usePushPromptChoreography(prompt, isAuthenticated);

  if (!oneSignalPush.isSupported) return null;

  return <WebPushPermissionDrawer prompt={prompt} />;
}
